import React, { useState, useEffect } from 'react';
import { Store, Plus, ExternalLink, TrendingUp, DollarSign, Package, AlertCircle, CheckCircle, XCircle, Clock, Trash2, RefreshCw, ChevronDown, ChevronUp, Pause, Play, Bell } from 'lucide-react';
import authService from '../services/AuthService';
import InfoTooltip from '../components/InfoTooltip';
import MegaExport from '../components/MegaExport';
import { MARKETPLACE_LOGOS, API_BASE } from '../consts';
import DealsViewer from '../components/DealsViewer';
import UpdateDealsOnboardingModal from '../components/UpdateDealsOnboardingModal';

// ===== On-demand live fetch helpers =====
const LIVE_FETCH_CHUNK = 7; // products per request; each group is awaited before the next starts

// Fetch live data for a group of product rows of ONE storefront. Never throws.
const fetchProductsChunk = async (storefrontId, productUrlIds) => {
    try {
        const res = await authService.authenticatedFetch(API_BASE + '/api/storefront/fetch-products', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ storefront_id: storefrontId, product_url_ids: productUrlIds })
        });
        const data = await res.json();
        if (res.ok) return { deals: data.deals || [], failed: data.failed || [] };
    } catch (e) {
        console.error('Live fetch failed:', e);
    }
    return { deals: [], failed: productUrlIds };
};

// Put fetched deals into a deals list IN PLACE of their skeleton (or old version on refresh),
// so list order is preserved. Marks failures, keeps the NEW flag.
const mergeFetchedDeals = (prev, chunkIds, result) => {
    const chunkSet = new Set(chunkIds);
    const failedSet = new Set(result.failed);
    const newPids = new Set(prev.filter(d => d.is_new && chunkSet.has(d.product_url_id)).map(d => d.product_id));
    const incoming = result.deals.map(d => (newPids.has(d.product_id) && !d.is_new) ? { ...d, is_new: true } : d);

    // Group incoming deals by their product row, so each group lands where that product already was
    const byUrlId = new Map();
    for (const d of incoming) {
        if (!byUrlId.has(d.product_url_id)) byUrlId.set(d.product_url_id, []);
        byUrlId.get(d.product_url_id).push(d);
    }
    const incomingIds = new Set(incoming.map(d => d.id));
    const placed = new Set();
    const out = [];

    for (const d of prev) {
        const inChunk = chunkSet.has(d.product_url_id);
        if (inChunk && byUrlId.has(d.product_url_id)) {
            if (!placed.has(d.product_url_id)) {
                out.push(...byUrlId.get(d.product_url_id)); // replace skeleton / old version in place
                placed.add(d.product_url_id);
            }
            continue;
        }
        if (incomingIds.has(d.id)) continue;
        if (d.is_skeleton && inChunk) {
            if (failedSet.has(d.product_url_id)) out.push({ ...d, fetch_failed: true });
            continue;
        }
        out.push(d);
    }
    // Safety: any deal that couldn't be matched to a position goes at the end
    for (const [k, list] of byUrlId) if (!placed.has(k)) out.push(...list);
    return out;
};

const LiveFetchBanner = ({ done, total }) => (
    <div className="bg-blue-50 border-2 border-blue-300 rounded-xl px-6 py-4 mb-4 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="animate-spin text-blue-600" size={24} />
            <span className="text-base font-bold text-blue-800">Loading live product data... {done}/{total}</span>
            <span className="text-sm font-semibold text-blue-600 ml-auto">
                {total > 0 ? Math.round((done / total) * 100) : 0}%
            </span>
        </div>
        <div className="w-full bg-blue-100 rounded-full h-3 overflow-hidden">
            <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }}
            />
        </div>
        <p className="text-xs text-blue-700 mt-2">
            Products fill in as they load. Filters and sorting apply to loaded products. Stay on this page to keep loading.
        </p>
    </div>
);

const StorefrontScanPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('create');
    const [storefronts, setStorefronts] = useState([]);
    const [selectedStorefront, setSelectedStorefront] = useState(null);
    const [loadingStorefronts, setLoadingStorefronts] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const currentPageRef = React.useRef(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalStorefronts, setTotalStorefronts] = useState(0);
    const [pageSize, setPageSize] = useState(7);
    const [pendingAutoSelect, setPendingAutoSelect] = useState(null);
    const [prefilledId, setPrefilledId] = useState(null);
    const [prefilledMarketplace, setPrefilledMarketplace] = useState(null);
    const pageSizeRef = React.useRef(7);

    // Combined "all stores" deals view
    const [combinedDeals, setCombinedDeals] = useState([]);
    const [combinedDealsMode, setCombinedDealsMode] = useState(null); // null | 'all' | 'new'
    const [combinedDealsLoading, setCombinedDealsLoading] = useState(false);
    const [combinedDealsProgress, setCombinedDealsProgress] = useState({ scanned: 0, total: 0 });
    const combinedRequestIdRef = React.useRef(0);
    const [combinedFetchProgress, setCombinedFetchProgress] = useState({ done: 0, total: 0 });
    useEffect(() => () => { combinedRequestIdRef.current++; }, []); // stop combined loading when leaving the page

    // Storefront search + marketplace filter — searches across ALL tracked storefronts, not just the current page
    const [searchQuery, setSearchQuery] = useState('');
    const [marketplaceFilter, setMarketplaceFilter] = useState('all'); // 'all' | 'amazon' | 'walmart' | 'ebay'
    const [filteredResults, setFilteredResults] = useState(null); // null = not filtering, array = matches across all pages
    const [filterLoading, setFilterLoading] = useState(false);
    const filterRequestIdRef = React.useRef(0);

    useEffect(() => {
        const q = searchQuery.trim();
        const isFilterActive = q.length > 0 || marketplaceFilter !== 'all';

        if (!isFilterActive) {
            setFilteredResults(null);
            setFilterLoading(false);
            return;
        }

        const requestId = ++filterRequestIdRef.current;
        const timer = setTimeout(async () => {
            setFilterLoading(true);
            try {
                const all = await loadAllTrackedStorefronts();
                if (requestId !== filterRequestIdRef.current) return; // superseded by newer input

                const needle = q.toLowerCase();
                const results = all.filter(s => {
                    const matchesQuery = !needle ||
                        (s.store_name || '').toLowerCase().includes(needle) ||
                        (s.storefront_url || '').toLowerCase().includes(needle);
                    const matchesMarketplace = marketplaceFilter === 'all' || s.marketplace === marketplaceFilter;
                    return matchesQuery && matchesMarketplace;
                });
                setFilteredResults(results);
            } finally {
                if (requestId === filterRequestIdRef.current) setFilterLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchQuery, marketplaceFilter]);

    useEffect(() => {
        if (!pendingAutoSelect || storefronts.length === 0) return;

        const match = storefronts.find(s => s.id === pendingAutoSelect.id);
        if (match) {
            setSelectedStorefront({ ...match, showNewOnly: pendingAutoSelect.newOnly });
            setPendingAutoSelect(null);
            setTimeout(() => scrollToDeals(), 100);
        } else if (hasMore) {
            // Not found yet, load next page
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            currentPageRef.current = nextPage;
            loadStorefronts(false, nextPage);
        }
        // If no hasMore and not found, it simply doesn't exist
    }, [storefronts, pendingAutoSelect]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlId = params.get('ID');
        const isNew = params.get('new') === '1';
        const isSearch = params.get('search') === '1';
        const urlMarketplace = params.get('marketplace');

        loadStorefronts();

        if (urlId && isSearch) {
            // Just pre-fill the create tab, don't navigate away
            setActiveTab('create');
            // We'll pass these as props to CreateStorefrontTab
            setPrefilledId(urlId);
            setPrefilledMarketplace(urlMarketplace || 'amazon');
        } else if (urlId) {
            setActiveTab('storefronts');
            setPendingAutoSelect({ id: urlId, newOnly: isNew });
        }
    }, []);

    const loadStorefronts = async (silent = false, page = currentPageRef.current, size = pageSizeRef.current) => {
        if (!silent) setLoadingStorefronts(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/storefront/tracked?page=${page}&page_size=${size}`
            );
            const data = await response.json();
            if (response.ok) {
                setStorefronts(data.storefronts || []);
                setHasMore(data.has_more || false);
                setTotalStorefronts(data.total || 0);
            }
        } catch (error) {
            console.error('Failed to load storefronts:', error);
        } finally {
            if (!silent) setLoadingStorefronts(false);
        }
    };

    const scrollToDeals = () => {
        const dealsSection = document.getElementById('deals-section');
        if (dealsSection) {
            dealsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Fetch every tracked storefront across all pages (not just the current page shown in the UI)
    const loadAllTrackedStorefronts = async () => {
        let all = [];
        let page = 1;
        const size = 100;
        while (true) {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/storefront/tracked?page=${page}&page_size=${size}`
            );
            const data = await response.json();
            if (!response.ok) break;
            all = all.concat(data.storefronts || []);
            if (!data.has_more) break;
            page++;
        }
        return all;
    };

    // Identifies "the same product" across stores: marketplace + product_id (e.g. ASIN for Amazon, item id for Walmart)
    const dealKey = (deal) =>
        `${(deal.marketplace || '').toLowerCase()}:${deal.product_id || deal.upc || deal.id}`;

    const loadCombinedDeals = async (filterNew, onlyIds = null) => {
        const requestId = ++combinedRequestIdRef.current;
        setSelectedStorefront(null); // close single-store view if open
        setCombinedDealsMode(onlyIds ? 'selected' : filterNew ? 'new' : 'all');
        setCombinedDeals([]);
        setCombinedDealsLoading(true);
        setCombinedDealsProgress({ scanned: 0, total: 0 });
        setCombinedFetchProgress({ done: 0, total: 0 });
        setTimeout(() => scrollToDeals(), 100);

        const merged = new Map();
        const publish = () => setCombinedDeals(
            [...merged.values()]
                .sort((a, b) => {
                    const aHasSource = a.supplier_name ? 1 : 0;
                    const bHasSource = b.supplier_name ? 1 : 0;
                    if (aHasSource !== bHasSource) return bHasSource - aHasSource; // source found comes first
                    return b.storefronts.size - a.storefronts.size; // then by store count
                })
                .map(d => ({
                    ...d,
                    found_in_stores_count: d.storefronts.size,
                    found_in_stores: [...d.storefronts],
                }))
        );

        try {
            const allStores = await loadAllTrackedStorefronts();
            const stores = onlyIds ? allStores.filter(s => onlyIds.includes(s.id)) : allStores;
            if (requestId !== combinedRequestIdRef.current) return; // superseded by a newer click

            setCombinedDealsProgress({ scanned: 0, total: stores.length });

            // Phase 1: load every store's list (fetched deals + skeletons for not-yet-fetched products)
            for (let i = 0; i < stores.length; i++) {
                if (requestId !== combinedRequestIdRef.current) return; // superseded, stop fetching

                const store = stores[i];
                const storeName = store.store_name || store.storefront_url;
                try {
                    const response = await authService.authenticatedFetch(
                        API_BASE + `/api/storefront/deals/${store.id}?include_pending=1`
                    );
                    const data = await response.json();
                    if (response.ok) {
                        let deals = data.deals || [];
                        if (filterNew) deals = deals.filter(d => d.is_new);

                        for (const deal of deals) {
                            const key = dealKey(deal);
                            const existing = merged.get(key);
                            if (existing) {
                                existing.storefronts.add(storeName);
                                // Another store already has this product fetched -> no need to fetch it again
                                if (existing.is_skeleton && !deal.is_skeleton) {
                                    merged.set(key, { ...deal, is_new: deal.is_new || existing.is_new, storefronts: existing.storefronts });
                                }
                            } else {
                                merged.set(key, { ...deal, storefronts: new Set([storeName]) });
                            }
                        }

                        // Mark this store's new products as seen, same as the single-store "new only" view does
                        if (filterNew && deals.length > 0) {
                            authService.authenticatedFetch(
                                API_BASE + `/api/storefront/deals/${store.id}/mark-seen`,
                                { method: 'POST' }
                            ).catch(() => { });
                        }
                    }
                } catch (error) {
                    console.error(`Failed to load deals for storefront ${store.id}:`, error);
                }

                if (requestId !== combinedRequestIdRef.current) return;
                publish();
                setCombinedDealsProgress({ scanned: i + 1, total: stores.length });
            }

            // Phase 2 (on-demand): fetch live data for products not fetched yet, LIVE_FETCH_CHUNK at a time per storefront
            setCombinedDealsLoading(false);
            // Same order the list shows skeletons in (most stores first), so rows fill top to bottom.
            // Each request is one store: take the next skeleton, plus the next ones from that same store.
            const orderedKeys = [...merged.entries()]
                .filter(([, d]) => d.is_skeleton)
                .sort((a, b) => b[1].storefronts.size - a[1].storefronts.size)
                .map(([key]) => key);
            const byStore = new Map();
            for (const key of orderedKeys) {
                const sid = merged.get(key).storefront_id;
                if (!byStore.has(sid)) byStore.set(sid, []);
                byStore.get(sid).push(key);
            }
            const storePos = new Map();
            const taken = new Set();
            const chunks = [];
            for (const key of orderedKeys) {
                if (taken.has(key)) continue;
                const sid = merged.get(key).storefront_id;
                const queue = byStore.get(sid);
                let pos = storePos.get(sid) || 0;
                const keys = [];
                while (pos < queue.length && keys.length < LIVE_FETCH_CHUNK) {
                    const k = queue[pos++];
                    if (!taken.has(k)) { keys.push(k); taken.add(k); }
                }
                storePos.set(sid, pos);
                chunks.push({ sid, keys });
            }
            const total = chunks.reduce((n, c) => n + c.keys.length, 0);
            setCombinedFetchProgress({ done: 0, total });

            let done = 0;
            for (const chunk of chunks) {
                if (requestId !== combinedRequestIdRef.current) return;
                const urlIdToKey = new Map(chunk.keys.map(k => [merged.get(k).product_url_id, k]));
                const result = await fetchProductsChunk(chunk.sid, [...urlIdToKey.keys()]);
                if (requestId !== combinedRequestIdRef.current) return;

                for (const d of result.deals) {
                    const key = dealKey(d);
                    const existing = merged.get(key);
                    merged.set(key, { ...d, is_new: d.is_new || !!existing?.is_new, storefronts: existing ? existing.storefronts : new Set() });
                }
                for (const id of result.failed) {
                    const key = urlIdToKey.get(id);
                    const e = key && merged.get(key);
                    if (e && e.is_skeleton) merged.set(key, { ...e, fetch_failed: true });
                }

                done += chunk.keys.length;
                publish();
                setCombinedFetchProgress({ done, total });
            }
        } finally {
            if (requestId === combinedRequestIdRef.current) {
                setCombinedDealsLoading(false);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Store className="text-teal-600" size={40} />
                        Storefront Stalking
                    </h1>
                    <p className="text-gray-600">
                        Track competitor storefronts automatically. Get daily scans, discover new products, and find cheaper sources.
                    </p>
                </div>

                <div className="flex justify-end items-center gap-3 mb-4">
                    <button
                        onClick={() => loadCombinedDeals(false)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition"
                    >
                        Source All Deals Across All Stores
                    </button>
                    <button
                        onClick={() => loadCombinedDeals(true)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition"
                    >
                        Source NEW Deals Across All Stores
                    </button>
                    <MegaExport scanType="storefront" />
                </div>

                <StorefrontEmailSettings user={user} />

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="flex border-b">
                        <button
                            onClick={() => { setActiveTab('create'); setSelectedStorefront(null) }}
                            className={`flex-1 py-4 px-6 font-semibold transition ${activeTab === 'create'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Plus className="inline mr-2" size={20} />
                            Track New Storefront
                        </button>
                        <button
                            onClick={() => setActiveTab('storefronts')}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center gap-1 justify-center ${activeTab === 'storefronts'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Store className="inline mr-2" size={20} />
                            Tracked Storefronts ({loadingStorefronts ? <RefreshCw className="animate-spin" size={16} /> : totalStorefronts})
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'create' ? (
                            <CreateStorefrontTab
                                onStorefrontCreated={() => { loadStorefronts(); setActiveTab('storefronts'); }}
                                existingStorefronts={storefronts}
                                marketplacesSellOn={user?.marketplaces_sell_on || []}
                                prefilledId={prefilledId}
                                prefilledMarketplace={prefilledMarketplace}
                                user={user}
                            />
                        ) : (
                            <MyStorefrontsTab
                                storefronts={filteredResults !== null ? filteredResults : storefronts}
                                user={user}
                                setStorefronts={setStorefronts}
                                loadingStorefronts={loadingStorefronts}
                                currentPage={currentPage}
                                hasMore={hasMore}
                                totalStorefronts={totalStorefronts}
                                pageSize={pageSize}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    pageSizeRef.current = size;
                                    setCurrentPage(1);
                                    currentPageRef.current = 1;
                                    loadStorefronts(false, 1, size);
                                }}
                                onPageChange={(page) => {
                                    setCurrentPage(page);
                                    currentPageRef.current = page;
                                    loadStorefronts(false, page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                onStorefrontSelect={(storefront, newOnly = false) => {
                                    combinedRequestIdRef.current++; // stop combined loading/fetching
                                    setCombinedDealsMode(null); // close combined view if open
                                    setSelectedStorefront({ ...storefront, showNewOnly: newOnly });
                                    setTimeout(() => scrollToDeals(), 100);
                                }}
                                onStorefrontDeleted={loadStorefronts}
                                onSourceSelected={(ids) => loadCombinedDeals(false, ids)}
                                onAutoUpdate={() => loadStorefronts(true)}
                                isFiltering={filteredResults !== null}
                                filterLoading={filterLoading}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                marketplaceFilter={marketplaceFilter}
                                onMarketplaceFilterChange={setMarketplaceFilter}
                            />
                        )}
                    </div>
                </div>

                {/* Selected Storefront Details */}
                {selectedStorefront && (
                    <div id="deals-section">
                        <StorefrontDetails
                            storefront={selectedStorefront}
                            showNewOnly={selectedStorefront?.showNewOnly || false}
                            onClose={() => setSelectedStorefront(null)}
                            user={user}
                        />
                    </div>
                )}

                {/* Combined Deals Across All Stores */}
                {combinedDealsMode && (
                    <div id="deals-section" className="relative">
                        {combinedDealsLoading && (
                            <div className="bg-teal-50 border-2 border-teal-300 rounded-xl px-6 py-4 mb-4 shadow-sm">
                                <div className="flex items-center gap-3 mb-2">
                                    <RefreshCw className="animate-spin text-teal-600" size={28} />
                                    <span className="text-lg font-bold text-teal-800">
                                        Scanning stores... {combinedDealsProgress.scanned}/{combinedDealsProgress.total}
                                    </span>
                                    <span className="text-sm font-semibold text-teal-600 ml-auto">
                                        {combinedDealsProgress.total > 0
                                            ? Math.round((combinedDealsProgress.scanned / combinedDealsProgress.total) * 100)
                                            : 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-teal-100 rounded-full h-3 overflow-hidden">
                                    <div
                                        className="bg-teal-600 h-3 rounded-full transition-all duration-300 ease-out"
                                        style={{
                                            width: `${combinedDealsProgress.total > 0
                                                ? (combinedDealsProgress.scanned / combinedDealsProgress.total) * 100
                                                : 0}%`
                                        }}
                                    />
                                </div>
                                {combinedDeals.length > 0 && (
                                    <p className="text-sm text-teal-700 mt-2">
                                        {combinedDeals.length} deal{combinedDeals.length !== 1 ? 's' : ''} found so far...
                                    </p>
                                )}
                            </div>
                        )}

                        {combinedFetchProgress.total > 0 && combinedFetchProgress.done < combinedFetchProgress.total && (
                            <LiveFetchBanner done={combinedFetchProgress.done} total={combinedFetchProgress.total} />
                        )}

                        <div className="relative">
                            <button
                                onClick={() => { combinedRequestIdRef.current++; setCombinedDealsMode(null); }}
                                className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"
                            >
                                ✕
                            </button>
                            <DealsViewer
                                deals={combinedDeals}
                                loading={combinedDealsLoading && combinedDeals.length === 0}
                                title={combinedDealsMode === 'new' ? 'New Deals — All Stores' : combinedDealsMode === 'selected' ? 'Deals — Selected Stores' : 'All Deals — All Stores'}
                                showFilters={true}
                                pageName="storefront"
                                user={user}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Create Storefront Tab Component
const CreateStorefrontTab = ({ onStorefrontCreated, existingStorefronts, marketplacesSellOn = [], prefilledId = null, prefilledMarketplace = null, user }) => {
    const [storefrontUrl, setStorefrontUrl] = useState(prefilledId ? prefilledId : '');
    const [marketplace, setMarketplace] = useState(prefilledMarketplace ? prefilledMarketplace : 'amazon');
    const [loading, setLoading] = useState(false);

    // Auto-fill and trigger search when prefilled params arrive
    useEffect(() => {
        if (prefilledId && prefilledMarketplace) {
            setStorefrontUrl(prefilledId);
            setMarketplace(prefilledMarketplace);
        }
    }, [prefilledId, prefilledMarketplace]);

    useEffect(() => {
        if (storefrontUrl && marketplace && prefilledId) {
            handleAddStorefront(
                prefilledId,
                prefilledMarketplace
            );

            window.history.replaceState(
                {},
                document.title,
                window.location.pathname
            );
        }
    }, [storefrontUrl, marketplace]);

    const amazonDomain = { 'US': '.com', 'UK': '.co.uk', 'CA': '.ca', 'MX': '.com.mx' }[user?.amazon_marketplace || 'US'] || '.com';
    const MARKETPLACES_PATTERNS = {
        'amazon': `https://www.amazon${amazonDomain}/s?me=XXXXXXXXXXXX&marketplaceID=ATVPDKIKX0DER`,
        'walmart': 'https://www.walmart.com/global/seller/XXXXXXXXXXXX/cp/shopall',
        'ebay': 'https://www.ebay.com/str/XXXXXXXXXXXX',
    }

    const handleAddStorefront = async (
        passedStorefrontUrl = null,
        passedMarketplace = null
    ) => {
        const rawInput = (typeof passedStorefrontUrl === 'string' ? passedStorefrontUrl : storefrontUrl);
        const lines = rawInput.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 100);

        if (lines.length === 0 && !prefilledId) {
            alert('Please enter at least one storefront URL or ID');
            return;
        }

        const selectedMarketplace = passedMarketplace || marketplace;
        setLoading(true);

        let successCount = 0;
        let errors = [];

        for (const line of lines) {
            let storefrontUrlTemp = line;
            if (!storefrontUrlTemp.startsWith('http://') && !storefrontUrlTemp.startsWith('https://')) {
                if (Object.keys(MARKETPLACES_PATTERNS).includes(selectedMarketplace)) {
                    storefrontUrlTemp = MARKETPLACES_PATTERNS[selectedMarketplace].replace('XXXXXXXXXXXX', storefrontUrlTemp);
                } else {
                    errors.push(`${line}: Invalid URL`);
                    continue;
                }
            } else {
                try {
                    const url = new URL(storefrontUrlTemp);
                    const domainParts = url.hostname.split('.');
                    const domainName = domainParts.length >= 2
                        ? domainParts[domainParts.length - 2].toLowerCase()
                        : '';
                    if (domainName !== selectedMarketplace.toLowerCase()) {
                        errors.push(`${line}: Not a ${selectedMarketplace} URL`);
                        continue;
                    }
                } catch {
                    errors.push(`${line}: Invalid URL format`);
                    continue;
                }
            }

            if (existingStorefronts.some(s => s.storefront_url.toLowerCase() === storefrontUrlTemp.toLowerCase())) {
                errors.push(`${line}: Already tracked`);
                continue;
            }

            try {
                const response = await authService.authenticatedFetch(API_BASE + '/api/storefront/tracked', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ storefront_url: storefrontUrlTemp, marketplace: selectedMarketplace })
                });
                const data = await response.json();
                if (!response.ok) {
                    if (data.error === 'MAX_SCANS_REACHED') {
                        alert(data.message);
                        break;
                    }
                    errors.push(`${line}: ${data.error || 'Failed'}`);
                } else {
                    successCount++;
                }
            } catch {
                errors.push(`${line}: Network error`);
            }
        }

        setLoading(false);

        if (errors.length > 0) {
            alert(`Added ${successCount} storefront(s).\n\nSkipped:\n${errors.join('\n')}`);
        }

        if (successCount > 0) {
            setStorefrontUrl('');
            onStorefrontCreated();
        }
    };

    const getPattern = (storeId = null) => {
        if (!storeId) {
            return marketplace === 'ebay' ? MARKETPLACES_PATTERNS[marketplace].replace('XXXXXXXXXXXX', 'STORENAME') : MARKETPLACES_PATTERNS[marketplace];
        } else {
            return MARKETPLACES_PATTERNS[marketplace].replace('XXXXXXXXXXXX', storeId);
        }
    };

    return (
        <div className="space-y-6">
            {/* Marketplace Selection */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Marketplace
                </label>
                <div className="grid grid-cols-3 gap-4">
                    {['amazon', 'walmart', 'ebay'].map(mp => {
                        const sellsOn = marketplacesSellOn.map(m => m.toLowerCase()).includes(mp);
                        return (
                            <div key={mp} className="relative group">
                                <button
                                    onClick={() => sellsOn && setMarketplace(mp)}
                                    disabled={!sellsOn}
                                    className={`w-full p-4 border-2 rounded-lg transition-all ${!sellsOn
                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                        : marketplace === mp
                                            ? 'border-teal-600 bg-teal-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <img
                                        src={MARKETPLACE_LOGOS[mp]}
                                        alt={mp}
                                        className="w-12 h-12 object-contain mx-auto mb-2"
                                    />
                                    <div className="text-sm font-semibold capitalize">{mp}</div>

                                    {/* Big X overlay */}
                                    {!sellsOn && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                                            <span className="text-red-500 text-5xl font-bold opacity-60">✕</span>
                                        </div>
                                    )}
                                </button>

                                {/* Tooltip */}
                                {!sellsOn && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center">
                                        You haven't selected <span className="capitalize font-semibold">{mp}</span> in your settings.{' '}
                                        <a
                                            href="/settings"
                                            className="underline text-teal-300 pointer-events-auto"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Go to Settings
                                        </a>{' '}
                                        to enable it.
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                    </div>
                                )
                                }
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* URL Input */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Storefront URLs / IDs <span className="text-xs font-normal text-gray-500">(one per line, up to 100)</span>
                </label>
                <div className="flex gap-2 items-start">
                    <textarea
                        value={storefrontUrl}
                        onChange={(e) => {
                            const lines = e.target.value.split('\n');
                            if (lines.length <= 100) setStorefrontUrl(e.target.value);
                        }}
                        placeholder={getPattern() + '\n' + getPattern() + '\n...'}
                        rows={4}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-y font-mono text-sm"
                    />
                    <button
                        onClick={handleAddStorefront}
                        disabled={loading}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition font-semibold flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? (
                            <RefreshCw className="animate-spin" size={20} />
                        ) : (
                            <Plus size={20} />
                        )}
                        Track All
                    </button>
                </div>
                {storefrontUrl.split('\n').filter(l => l.trim()).length > 1 && (
                    <p className="text-xs text-gray-500 mt-1">{storefrontUrl.split('\n').filter(l => l.trim()).length} storefronts entered</p>
                )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <AlertCircle className="inline mr-2" size={16} />
                    <strong>How it works:</strong><br />Paste a competitor's storefront URL (or competitor's storefront ID) and we'll scan it daily automatically.
                    You'll be notified of new products, and can run reverse scans to find cheaper supplier sources!
                </p>
            </div>

            {/* Email Warning Box */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                    <AlertCircle className="inline mr-2" size={16} />
                    <strong>Check your inbox!</strong><br />
                    Daily storefront update emails may land in your <strong>Spam</strong>, <strong>Promotions</strong>, or <strong>Updates</strong> folder. Make sure to mark us as "Not Spam" so you never miss a new product alert!
                </p>
            </div>
        </div>
    );
};

// My Storefronts Tab Component
const MyStorefrontsTab = ({ onSourceSelected, storefronts, setStorefronts, loadingStorefronts, onStorefrontSelect, onStorefrontDeleted, onAutoUpdate, currentPage, hasMore, totalStorefronts, onPageChange, pageSize, onPageSizeChange, user, isFiltering, filterLoading, searchQuery, onSearchChange, marketplaceFilter, onMarketplaceFilterChange }) => {
    const [deletingId, setDeletingId] = useState(null);
    const [pausingId, setPausingId] = useState(null);
    const [resumingId, setResumingId] = useState(null);
    const [expandedStorefronts, setExpandedStorefronts] = useState(new Set());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [favorites, setFavorites] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem('sf_favorites') || '[]')); }
        catch { return new Set(); }
    });
    const toggleFavorite = (id) => {
        setFavorites(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            localStorage.setItem('sf_favorites', JSON.stringify([...next]));
            return next;
        });
    };
    const [notes, setNotes] = useState(() => {
        try { return JSON.parse(localStorage.getItem('sf_notes') || '{}'); }
        catch { return {}; }
    });
    const saveNote = (id, text) => {
        setNotes(prev => {
            const next = { ...prev, [id]: text };
            localStorage.setItem('sf_notes', JSON.stringify(next));
            return next;
        });
    };

    // Auto-refresh every 20 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            onAutoUpdate();
        }, 20000);

        return () => clearInterval(interval);
    }, []);

    const updateStorefrontStatus = (storefrontId, status) => {
        setStorefronts(prev => prev.map(s =>
            s.id === storefrontId ? { ...s, status } : s
        ));
    };

    const handleMarkAsSeen = async (storefrontId) => {
        try {
            await authService.authenticatedFetch(
                API_BASE + `/api/storefront/deals/${storefrontId}/mark-seen`,
                { method: 'POST' }
            );
            onAutoUpdate();
        } catch (error) {
            console.error('Failed to mark products as seen:', error);
        }
    };

    const handleDelete = async (storefrontId) => {
        if (!window.confirm('Delete this storefront? All associated deals will be deleted.')) {
            return;
        }

        setDeletingId(storefrontId);
        setStorefronts(prev => prev.filter(s => s.id !== storefrontId));
        try {
            await authService.authenticatedFetch(API_BASE + `/api/storefront/tracked/${storefrontId}`, {
                method: 'DELETE'
            });
            onStorefrontDeleted();
        } catch (error) {
            onStorefrontDeleted();
            alert('Failed to delete storefront');
        } finally {
            setDeletingId(null);
        }
    };

    const toggleSelect = (storefrontId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(storefrontId) ? next.delete(storefrontId) : next.add(storefrontId);
            return next;
        });
    };

    const allOnPageSelected = storefronts.length > 0 && storefronts.every(s => selectedIds.has(s.id));

    const toggleSelectAll = () => {
        setSelectedIds(prev => {
            if (allOnPageSelected) {
                // Deselect everything currently on this page
                const next = new Set(prev);
                storefronts.forEach(s => next.delete(s.id));
                return next;
            } else {
                // Select everything currently on this page
                const next = new Set(prev);
                storefronts.forEach(s => next.add(s.id));
                return next;
            }
        });
    };

    const handleBulkDelete = async () => {
        const ids = [...selectedIds];
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} storefront(s)? All associated deals will be deleted.`)) {
            return;
        }

        setIsBulkDeleting(true);
        setStorefronts(prev => prev.filter(s => !selectedIds.has(s.id)));

        const results = await Promise.allSettled(
            ids.map(id =>
                authService.authenticatedFetch(API_BASE + `/api/storefront/tracked/${id}`, {
                    method: 'DELETE'
                })
            )
        );

        const failedCount = results.filter(r => r.status === 'rejected').length;

        setSelectedIds(new Set());
        setIsBulkDeleting(false);
        onStorefrontDeleted();

        if (failedCount > 0) {
            alert(`${failedCount} storefront(s) failed to delete. The list has been refreshed.`);
        }
    };

    const handlePause = async (storefrontId) => {
        setPausingId(storefrontId);
        try {
            await authService.authenticatedFetch(API_BASE + `/api/storefront/tracked/${storefrontId}/pause`, {
                method: 'POST'
            });
            updateStorefrontStatus(storefrontId, 'paused');
            onAutoUpdate();
        } catch (error) {
            alert('Failed to pause storefront');
        } finally {
            setPausingId(null);
        }
    };

    const handleResume = async (storefrontId) => {
        setResumingId(storefrontId);
        try {
            const response = await authService.authenticatedFetch(API_BASE + `/api/storefront/tracked/${storefrontId}/resume`, {
                method: 'POST'
            });
            const data = await response.json();
            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    alert(data.message);
                    return;
                }
                throw new Error(data.error || 'Failed to resume storefront');
            }
            updateStorefrontStatus(storefrontId, 'active');
            onAutoUpdate();
        } catch (error) {
            alert(error.message || 'Failed to resume storefront');
        } finally {
            setResumingId(null);
        }
    };

    const toggleMinimize = (storefrontId) => {
        setExpandedStorefronts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(storefrontId)) {
                newSet.delete(storefrontId);
            } else {
                newSet.add(storefrontId);
            }
            return newSet;
        });
    };

    if (loadingStorefronts) {
        return (
            <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                <p className="text-gray-600 text-lg font-semibold">Loading storefronts...</p>
            </div>
        );
    }

    if (storefronts.length === 0 && !isFiltering) {
        return (
            <div className="text-center py-12">
                <Store className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">No tracked storefronts yet</p>
                <p className="text-gray-500 text-sm">Add your first storefront to get started!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 pb-2">
                <div className="relative flex-1 min-w-[220px]">
                    <input
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search by store name or URL…"
                        className="w-full px-3 py-2 pr-8 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => onSearchChange('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    {['all', 'amazon', 'walmart', 'ebay'].map(mp => (
                        <button
                            key={mp}
                            onClick={() => onMarketplaceFilterChange(mp)}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${marketplaceFilter === mp
                                ? 'bg-white text-teal-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {mp}
                        </button>
                    ))}
                </div>
                {filterLoading && (
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                        <RefreshCw className="animate-spin" size={13} /> Searching all storefronts…
                    </span>
                )}
                {isFiltering && !filterLoading && (
                    <span className="text-xs text-gray-500">
                        {storefronts.length} match{storefronts.length !== 1 ? 'es' : ''}
                    </span>
                )}
            </div>

            {isFiltering && !filterLoading && storefronts.length === 0 ? (
                <div className="text-center py-12">
                    <Store className="mx-auto text-gray-400 mb-4" size={64} />
                    <p className="text-gray-600 text-lg">No storefronts match your search</p>
                    <p className="text-gray-500 text-sm">Try a different name, URL, or marketplace filter.</p>
                </div>
            ) : (
                <>
                    {storefronts.length > 0 && (
                        <div className="flex items-center justify-between px-1">
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={allOnPageSelected}
                                    onChange={toggleSelectAll}
                                    className="accent-teal-600 w-4 h-4"
                                />
                                {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all on this page'}
                            </label>

                            {selectedIds.size > 0 && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setSelectedIds(new Set())}
                                        className="text-sm text-gray-500 hover:text-gray-700"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        onClick={() => onSourceSelected([...selectedIds])}
                                        className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition flex items-center gap-2"
                                    >
                                        <TrendingUp size={16} />
                                        Source From Selected ({selectedIds.size})
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={isBulkDeleting}
                                        className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {isBulkDeleting ? <RefreshCw className="animate-spin" size={16} /> : <Trash2 size={16} />}
                                        Delete Selected ({selectedIds.size})
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {[...storefronts].sort((a, b) => {
                        const aFav = favorites.has(a.id) ? 0 : 1;
                        const bFav = favorites.has(b.id) ? 0 : 1;
                        if (aFav !== bFav) return aFav - bFav;
                        const order = { scanning: 0, active: 1, paused: 2 };
                        return (order[a.status] ?? 5) - (order[b.status] ?? 5);
                    }).map(storefront => (
                        <StorefrontCard
                            key={storefront.id}
                            storefront={storefront}
                            user={user}
                            isChecked={selectedIds.has(storefront.id)}
                            onToggleCheck={() => toggleSelect(storefront.id)}
                            onSelect={(newOnly = false) => onStorefrontSelect(storefront, newOnly)}
                            onDelete={() => handleDelete(storefront.id)}
                            onPause={() => handlePause(storefront.id)}
                            onResume={() => handleResume(storefront.id)}
                            isDeleting={deletingId === storefront.id}
                            isPausing={pausingId === storefront.id}
                            isResuming={resumingId === storefront.id}
                            isMinimized={!expandedStorefronts.has(storefront.id)}
                            onToggleMinimize={() => toggleMinimize(storefront.id)}
                            handleMarkAsSeen={() => handleMarkAsSeen(storefront.id)}
                            isFavorite={favorites.has(storefront.id)}
                            onToggleFavorite={() => toggleFavorite(storefront.id)}
                            note={notes[storefront.id] || ''}
                            onSaveNote={(text) => saveNote(storefront.id, text)}
                        />
                    ))}

                    <p className="text-xs text-gray-300 text-center" style={{ marginBottom: '-4px' }}>
                        Running slow? Email contact@multisellerpro.com and we'll speed things up for you.
                    </p>

                    {!isFiltering && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Storefronts per page:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-black bg-white"
                                >
                                    {[5, 7, 10, 20, 25, 50].map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            {(currentPage > 1 || hasMore) && (
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => onPageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        ← Previous
                                    </button>
                                    <span className="text-sm text-black">Page {currentPage}</span>
                                    <button
                                        onClick={() => onPageChange(currentPage + 1)}
                                        disabled={!hasMore}
                                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// Storefront Card Component
const StorefrontCard = ({ storefront, onSelect, onDelete, onPause, onResume, isDeleting, isPausing, isResuming, isMinimized, onToggleMinimize, handleMarkAsSeen, isFavorite, onToggleFavorite, note, onSaveNote, user, isChecked, onToggleCheck }) => {
    const marketplaceLogo = MARKETPLACE_LOGOS[storefront.marketplace];
    const [editingNote, setEditingNote] = useState(false);
    const [noteText, setNoteText] = useState(note);
    useEffect(() => { setNoteText(note); }, [note]);

    const getStatusBadge = () => {
        if (storefront.status === 'paused') {
            return <span className="flex items-center gap-1 text-gray-700 bg-gray-100 px-3 py-1 rounded-full text-sm font-semibold">
                <Pause size={16} /> Paused
            </span>;
        }
        if (storefront.status === 'scanning') {
            return <span className="flex items-center gap-1 text-blue-700 bg-blue-100 px-3 py-1 rounded-full text-sm font-semibold">
                <RefreshCw size={16} className="animate-spin" /> Scanning...
            </span>;
        }
        return <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full text-sm font-semibold">
            <CheckCircle size={16} /> Active
        </span>;
    };

    // Minimized View
    if (isMinimized) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition">
                <div className="p-4 cursor-pointer" onClick={onToggleMinimize}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => { e.stopPropagation(); onToggleCheck(); }}
                                onClick={(e) => e.stopPropagation()}
                                className="accent-teal-600 w-4 h-4"
                            />
                            <button onClick={onToggleMinimize} className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded">
                                <ChevronDown size={20} />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                                className="p-1 hover:bg-yellow-50 rounded"
                                title={isFavorite ? 'Unpin' : 'Pin to top'}
                            >
                                <span className="text-xl">{isFavorite ? '⭐' : '☆'}</span>
                            </button>

                            <div className="flex items-center gap-3">
                                {marketplaceLogo && (
                                    <img src={marketplaceLogo} alt={storefront.marketplace} className="w-8 h-8 object-contain" />
                                )}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        {storefront.store_name || 'Scanning Store...'}
                                        {editingNote ? (
                                            <input
                                                autoFocus
                                                value={noteText}
                                                onChange={(e) => setNoteText(e.target.value)}
                                                onBlur={() => { onSaveNote(noteText); setEditingNote(false); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { onSaveNote(noteText); setEditingNote(false); } if (e.key === 'Escape') setEditingNote(false); }}
                                                placeholder="Add a note..."
                                                className="text-sm font-normal border border-gray-300 rounded px-2 py-0.5 w-48 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span
                                                onClick={(e) => { e.stopPropagation(); setEditingNote(true); }}
                                                className="text-xs font-normal text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-0.5 rounded-full max-w-xs truncate cursor-pointer hover:bg-yellow-100"
                                                title="Click to edit note"
                                            >
                                                {note ? note : '✏️ note...'}
                                            </span>
                                        )}
                                    </h3>
                                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 max-w-md">
                                        <span className="truncate">{storefront.storefront_url}</span>
                                        <a
                                            href={storefront.storefront_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 inline-flex items-center"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-3 w-3 opacity-60 hover:opacity-100 hover:text-blue-600 transition-colors"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {getStatusBadge()}

                            {storefront.new_products_count > 0 && (
                                <div className="flex flex-col items-center gap-0.5">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSelect(true);
                                        }}
                                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 hover:scale-105 transition-all"
                                        title="Click to see new products"
                                    >
                                        <Bell size={16} />
                                        {storefront.new_products_count} New
                                    </button>
                                </div>
                            )}

                            <div className="text-center">
                                <div className="text-xs text-gray-600">Deals</div>
                                <div className="text-lg font-bold text-green-800">{storefront.total_deals || 0}</div>
                            </div>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect();
                                }}
                                className="bg-gradient-to-r from-teal-500 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition"
                            >
                                View Deals
                            </button>

                            {storefront.status === 'active' || storefront.status === 'scanning' ? (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onPause(); }}
                                    disabled={isPausing}
                                    className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded disabled:opacity-50"
                                >
                                    {isPausing ? <RefreshCw className="animate-spin" size={20} /> : <Pause size={20} />}
                                </button>
                            ) : (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onResume(); }}
                                    disabled={isResuming}
                                    className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded disabled:opacity-50"
                                >
                                    {isResuming ? <RefreshCw className="animate-spin" size={20} /> : <Play size={20} />}
                                </button>
                            )}

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                disabled={isDeleting}
                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                                {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Expanded View
    return (
        <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition p-6">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2 flex-1">
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={onToggleCheck}
                        className="accent-teal-600 w-4 h-4"
                    />
                    <button onClick={onToggleMinimize} className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded">
                        <ChevronUp size={20} />
                    </button>
                    <button
                        onClick={onToggleFavorite}
                        className="p-1 hover:bg-yellow-50 rounded"
                        title={isFavorite ? 'Unpin' : 'Pin to top'}
                    >
                        <span className="text-xl">{isFavorite ? '⭐' : '☆'}</span>
                    </button>

                    <div className="flex items-center gap-3">
                        {marketplaceLogo && (
                            <img src={marketplaceLogo} alt={storefront.marketplace} className="w-10 h-10 object-contain" />
                        )}
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">
                                {storefront.store_name || 'Scanning Store...'}
                            </h3>
                            {editingNote ? (
                                <input
                                    autoFocus
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    onBlur={() => { onSaveNote(noteText); setEditingNote(false); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { onSaveNote(noteText); setEditingNote(false); } if (e.key === 'Escape') setEditingNote(false); }}
                                    placeholder="Add a note..."
                                    className="text-sm border border-gray-300 rounded px-2 py-1 w-64 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                />
                            ) : (
                                <div
                                    onClick={() => setEditingNote(true)}
                                    className="text-sm text-gray-400 hover:text-gray-700 cursor-pointer flex items-center gap-1"
                                    title="Click to add note"
                                >
                                    ✏️ {note ? <span className="text-yellow-700">{note}</span> : 'Add note...'}
                                </div>
                            )}
                            <div className="text-sm text-gray-500">
                                Last scanned: {storefront.last_scanned_at ? new Date(storefront.last_scanned_at).toLocaleString() : 'Never'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {getStatusBadge()}

                    {storefront.status === 'active' || storefront.status === 'scanning' ? (
                        <button onClick={onPause} className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded">
                            <Pause size={20} />
                        </button>
                    ) : (
                        <button onClick={onResume} className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded">
                            <Play size={20} />
                        </button>
                    )}

                    <button onClick={onDelete} disabled={isDeleting} className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50">
                        {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                    </button>
                </div>
            </div>

            <div className="text-xs text-gray-500 mb-4 truncate">
                <a
                    href={storefront.storefront_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 group text-xs text-gray-500 hover:text-blue-600 transition-colors max-w-md"
                >
                    <span className="truncate">{storefront.storefront_url}</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
                {(storefront.seller_metadata.business_name || storefront.seller_metadata.address || storefront.seller_metadata.email || storefront.seller_metadata.phone_number || storefront.seller_metadata.deactivation_status || storefront.seller_metadata.username) && (
                    <div className='mt-1'>
                        {storefront.seller_metadata.business_name && <><strong>Business Name:</strong> {storefront.seller_metadata.business_name} | </>}
                        {storefront.seller_metadata.address && <><strong>Address:</strong> {storefront.seller_metadata.address} | </>}
                        {storefront.seller_metadata.email && <><strong>Email:</strong> {storefront.seller_metadata.email} | </>}
                        {storefront.seller_metadata.phone_number && <><strong>Phone:</strong> {storefront.seller_metadata.phone_number} | </>}
                        {storefront.marketplace === 'ebay' && storefront.seller_metadata.username && (
                            <><strong>Username:</strong> {storefront.seller_metadata.username} | </>
                        )}
                        {storefront.marketplace === 'ebay' && storefront.seller_metadata.store_url_id && (
                            <><strong>Store ID:</strong> {storefront.seller_metadata.store_url_id} | </>
                        )}
                        {storefront.marketplace === 'walmart' && storefront.seller_metadata.deactivation_status && (
                            <><strong>Status:</strong> <span className={storefront.seller_metadata.deactivation_status === 'ACTIVE' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{storefront.seller_metadata.deactivation_status}</span></>
                        )}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-5 gap-4 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded">
                    <div className="text-xs text-blue-600 font-semibold">{storefront.marketplace === 'ebay' ? "Items Sold" : "Total Products"}</div>
                    <div className="text-2xl font-bold text-blue-800">
                        {storefront.marketplace === 'walmart'
                            ? `${(storefront.seller_metadata.total_items || ((storefront.total_products || 0) * 40))}`
                            : storefront.marketplace === 'ebay'
                                ? (storefront.seller_metadata.items_sold || storefront.total_products || 0)
                                : (storefront.total_products || 0)
                        }
                    </div>
                </div>

                <div className="text-center p-3 bg-red-50 rounded border border-red-200 relative group">
                    <div className="text-xs text-red-600 font-semibold">New Products</div>
                    <div className="text-2xl font-bold text-red-800">{storefront.new_products_count}</div>
                    {storefront.new_products_count > 0 && (
                        <div className="text-xs text-red-400 mt-0.5">Click to mark as seen</div>
                    )}

                    {/* Mark as Seen Button - appears on hover */}
                    {storefront.new_products_count > 0 && (<button
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(true);
                        }}
                        className="absolute inset-0 bg-red-600 bg-opacity-0 hover:bg-opacity-90 transition-all flex items-center justify-center rounded opacity-0 group-hover:opacity-100"
                    >
                        <span className="text-white font-semibold text-sm">View New Products</span>
                    </button>)}
                </div>

                <div className="text-center p-3 bg-green-50 rounded">
                    <div className="text-xs text-green-600 font-semibold">Total Deals</div>
                    <div className="text-2xl font-bold text-green-800">{storefront.total_deals || 0}</div>
                </div>

                {/* eBay Specific Stats */}
                {storefront.marketplace === 'ebay' && (
                    <>
                        <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1 text-center">
                            <div className="text-xs text-purple-600 font-semibold">Followers</div>
                            <div className="text-2xl font-bold text-purple-800">
                                {storefront.seller_metadata.followers || 0}
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-center">
                            <div className="text-xs text-yellow-600 font-semibold">Feedback</div>
                            <div className="text-sm font-bold text-yellow-800">
                                <span className="text-xl">{storefront.seller_metadata.feedback_percentage?.toFixed(1) || 0}%</span>
                                <span className="text-xs text-gray-500 ml-1 block">(positive)</span>
                            </div>
                        </div>
                    </>
                )}

                {storefront.marketplace === 'amazon' && storefront.seller_metadata.has_fba !== undefined && <div className="text-center p-3 bg-orange-50 rounded">
                    <div className="text-xs text-orange-600 font-semibold">FBA</div>
                    <div className="text-2xl font-bold text-orange-800">{storefront.seller_metadata.has_fba ? "✓" : "X"}</div>
                </div>}

                {storefront.marketplace === 'amazon' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-center">
                        <div className="text-xs text-yellow-600 font-semibold">Ratings</div>
                        <div className="text-sm font-bold text-yellow-800">
                            {storefront.seller_metadata.rating_count_30d}
                            <span className="text-xs text-gray-500 ml-1">(30 Days)</span>
                        </div>
                        <div className="text-sm font-bold text-yellow-800">
                            {storefront.seller_metadata.rating_count_lifetime}
                            <span className="text-xs text-gray-500 ml-1">(Lifetime)</span>
                        </div>
                    </div>
                )}

                {storefront.marketplace === 'walmart' && storefront.seller_metadata.has_badge !== undefined && <div className="text-center p-3 bg-orange-50 rounded">
                    <div className="text-xs text-orange-600 font-semibold">Seller Badge</div>
                    <div className="text-2xl font-bold text-orange-800">{storefront.seller_metadata.has_badge ? "✓" : "X"}</div>
                </div>}

                {storefront.marketplace === 'walmart' && storefront.seller_metadata && storefront.seller_metadata.average_rating && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-center">
                        <div className="text-xs text-yellow-600 font-semibold">Ratings</div>
                        <div className="text-sm font-bold text-yellow-800">
                            <span className="text-xl">{storefront.seller_metadata.average_rating.toFixed(1)}/5</span>
                            <span className="text-xs text-gray-500 ml-1 block">({storefront.seller_metadata.total_review_count} reviews)</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Amazon Seller Metadata - Only show if expanded and marketplace is Amazon */}
            {storefront.marketplace === 'amazon' && storefront.seller_metadata && (
                <div className="mt-3 mb-3 grid grid-cols-3 gap-2">
                    {/* Top Categories - Show all above 5% + Others */}
                    {storefront.seller_metadata.category_statistics?.length > 0 && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded p-2">
                            <div className="text-xs font-semibold text-blue-800 mb-1.5">Top Categories</div>
                            <div className="space-y-0.5">
                                {(() => {
                                    const categories = storefront.seller_metadata.category_statistics;
                                    const above5 = categories.filter(cat => cat.percentage >= 5);
                                    const below5 = categories.filter(cat => cat.percentage < 5);
                                    const othersTotal = below5.reduce((sum, cat) => sum + cat.percentage, 0);
                                    const othersCount = below5.reduce((sum, cat) => sum + cat.product_count, 0);

                                    return (
                                        <>
                                            {above5.map((cat, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-700">{cat.category_name || `Category ${cat.category_id}`}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-blue-800">{cat.percentage.toFixed(1)}%</span>
                                                        <span className="text-gray-500">({cat.product_count})</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {below5.length > 0 && (
                                                <div className="flex items-center justify-between text-xs border-t border-blue-100 pt-0.5 mt-0.5">
                                                    <span className="text-gray-600 italic">Others ({below5.length} categories)</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-blue-700">{othersTotal.toFixed(1)}%</span>
                                                        <span className="text-gray-500">({othersCount})</span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Top Brands - Show all above 5% + Others */}
                    {storefront.seller_metadata.brand_statistics?.length > 0 && (
                        <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded p-2">
                            <div className="text-xs font-semibold text-green-800 mb-1.5">Top Brands</div>
                            <div className="space-y-0.5">
                                {(() => {
                                    const brands = storefront.seller_metadata.brand_statistics;
                                    const above5 = brands.filter(b => b.percentage >= 5);
                                    const below5 = brands.filter(b => b.percentage < 5);
                                    const othersTotal = below5.reduce((sum, b) => sum + b.percentage, 0);
                                    const othersCount = below5.reduce((sum, b) => sum + b.product_count, 0);

                                    return (
                                        <>
                                            {above5.map((brand, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-700 capitalize">{brand.brand}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-green-800">{brand.percentage.toFixed(1)}%</span>
                                                        <span className="text-gray-500">({brand.product_count})</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {below5.length > 0 && (
                                                <div className="flex items-center justify-between text-xs border-t border-green-100 pt-0.5 mt-0.5">
                                                    <span className="text-gray-600 italic">Others ({below5.length} brands)</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-green-700">{othersTotal.toFixed(1)}%</span>
                                                        <span className="text-gray-500">({othersCount})</span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Top Competitors - Show all above 5% + Others - Clickable links */}
                    {storefront.seller_metadata.competitors?.length > 0 && (
                        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-2">
                            <div className="text-xs font-semibold text-red-800 mb-1.5">Top Competitors</div>
                            <div className="space-y-0.5">
                                {(() => {
                                    const competitors = storefront.seller_metadata.competitors;
                                    const above5 = competitors.filter(c => c.percent >= 5);
                                    const below5 = competitors.filter(c => c.percent < 5);
                                    const othersTotal = below5.reduce((sum, c) => sum + c.percent, 0);

                                    return (
                                        <>
                                            {above5.map((comp, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs gap-2">
                                                    <a
                                                        href={`https://www.amazon${{ 'US': '.com', 'UK': '.co.uk', 'CA': '.ca', 'MX': '.com.mx' }[user?.amazon_marketplace || 'US'] || '.com'}/sp?seller=${comp.sellerId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 underline font-mono text-xs truncate"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {comp.sellerId}
                                                    </a>
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        <span className="font-semibold text-red-800">{comp.percent}% <span className='text-xs text-gray-500 ml-1'>(overlap)</span></span>
                                                        <a
                                                            href={`/storefront-scan?ID=${encodeURIComponent(comp.sellerId)}&search=1&marketplace=amazon`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            title="Add this seller to Storefront Scan"
                                                            className="flex items-center gap-0.5 px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white text-[10px] font-semibold rounded-full transition"
                                                        >
                                                            <Plus size={10} /> Track
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                            {below5.length > 0 && (
                                                <div className="flex items-center justify-between text-xs border-t border-red-100 pt-0.5 mt-0.5">
                                                    <span className="text-gray-600 italic">Others ({below5.length} sellers)</span>
                                                    <span className="font-semibold text-red-700">{othersTotal.toFixed(1)}%</span>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                }}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
            >
                <TrendingUp size={20} />
                View Deals
            </button>
        </div>
    );
};

// Storefront Details Component (uses DealsViewer!)
const StorefrontDetails = ({ storefront, onClose, user, showNewOnly }) => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tempDeals, setTempDeals] = useState([]);
    const [hasNewDeals, setHasNewDeals] = useState(false);
    const [ignoredNewDeals, setIgnoredNewDeals] = useState(false);
    const [pendingIncoming, setPendingIncoming] = useState([]);
    const [newOnlyFilter, setNewOnlyFilter] = useState(showNewOnly);
    // On-demand live fetch
    const [liveProgress, setLiveProgress] = useState({ done: 0, total: 0 });
    const liveRunRef = React.useRef(0);
    const liveRunning = liveProgress.total > 0 && liveProgress.done < liveProgress.total;

    useEffect(() => {
        setDeals([]);
        setTempDeals([]);
        setHasNewDeals(false);
        setNewOnlyFilter(showNewOnly);
        loadDeals(false, showNewOnly);
        const interval = setInterval(() => loadDeals(true), 20000);
        return () => {
            clearInterval(interval);
            liveRunRef.current++; // stop live fetching when closing / switching store
        };
    }, [storefront.id]);

    // Fetch live data LIVE_FETCH_CHUNK at a time (each group awaited). Stops when superseded or unmounted.
    const startLiveFetch = async (productUrlIds) => {
        const runId = ++liveRunRef.current;
        const ids = [...new Set(productUrlIds.filter(Boolean))];
        setLiveProgress({ done: 0, total: ids.length });
        for (let i = 0; i < ids.length; i += LIVE_FETCH_CHUNK) {
            if (runId !== liveRunRef.current) return;
            const chunk = ids.slice(i, i + LIVE_FETCH_CHUNK);
            const result = await fetchProductsChunk(storefront.id, chunk);
            if (runId !== liveRunRef.current) return;
            setDeals(prev => mergeFetchedDeals(prev, chunk, result));
            setLiveProgress({ done: Math.min(i + LIVE_FETCH_CHUNK, ids.length), total: ids.length });
        }
    };

    const refreshAllLive = () => {
        const ids = [...new Set(deals.map(d => d.product_url_id).filter(Boolean))];
        if (ids.length === 0) return;
        if (!window.confirm(`Fetch fresh live data for ${ids.length} product(s)? This may take a while.`)) return;
        startLiveFetch(ids);
    };

    const loadDeals = async (silent = false, filterNew = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/storefront/deals/${storefront.id}` + (silent ? '' : '?include_pending=1')
            );
            const data = await response.json();
            if (response.ok) {
                let incoming = data.deals || [];
                if (filterNew) {
                    incoming = incoming.filter(d => d.is_new);
                    // mark as seen silently after showing
                    authService.authenticatedFetch(
                        API_BASE + `/api/storefront/deals/${storefront.id}/mark-seen`,
                        { method: 'POST' }
                    ).catch(() => { });
                }

                if (!silent) {
                    // Full (re)load: show everything now (skeletons for not-yet-fetched products), then fetch them live
                    setDeals(incoming);
                    setTempDeals([]);
                    setPendingIncoming([]);
                    setHasNewDeals(false);
                    startLiveFetch(incoming.filter(d => d.is_skeleton).map(d => d.product_url_id));
                    return;
                }

                // Silent 20s sync (fetched deals only — e.g. reverse scan results)
                setDeals(prev => {
                    if (prev.length === 0) return incoming;
                    const incomingById = new Map(incoming.map(d => [d.id, d]));
                    const incomingByPid = new Map(incoming.map(d => [d.product_id, d]));
                    const updated = prev.map(d => {
                        const match = d.is_skeleton ? incomingByPid.get(d.product_id) : incomingById.get(d.id);
                        if (!match) return d;
                        return (d.is_new && !match.is_new) ? { ...match, is_new: true } : match;
                    });
                    const existingIds = new Set(updated.map(d => d.id));
                    const newOnes = incoming.filter(d => !existingIds.has(d.id));
                    if (newOnes.length > 0) {
                        setTempDeals(newOnes);
                        setPendingIncoming(incoming);
                        setHasNewDeals(true);
                    }
                    return updated;
                });
            }
        } catch (error) {
            console.error('Failed to load deals:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    return (
        <div className="relative">
            <UpdateDealsOnboardingModal show={hasNewDeals} scanType="storefront" />
            {hasNewDeals && !ignoredNewDeals && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4">
                    <span>🎉 {tempDeals.length} new deal{tempDeals.length > 1 ? 's' : ''} found!</span>
                    <button
                        onClick={() => {
                            const incomingIds = new Set(pendingIncoming.map(d => d.id));
                            setDeals(prev => [
                                ...tempDeals,
                                ...prev.filter(d => incomingIds.has(d.id) || d.is_skeleton)
                            ]);
                            setTempDeals([]);
                            setPendingIncoming([]);
                            setHasNewDeals(false);
                        }}
                        className="bg-teal-500 hover:bg-teal-400 px-3 py-1 rounded-full text-sm font-semibold"
                    >
                        Update
                    </button>
                    <button
                        onClick={() => {
                            setIgnoredNewDeals(true);
                            setHasNewDeals(false);
                        }}
                        className="text-gray-400 hover:text-white text-sm"
                    >
                        ✕
                    </button>
                </div>
            )}
            {newOnlyFilter && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-2 flex items-center justify-between">
                    <span className="text-sm text-yellow-800 font-semibold">🆕 Showing new products only</span>
                    <button
                        onClick={() => { setNewOnlyFilter(false); loadDeals(false, false); }}
                        className="text-sm text-blue-600 hover:underline"
                    >
                        Show all deals →
                    </button>
                </div>
            )}
            {liveRunning && <LiveFetchBanner done={liveProgress.done} total={liveProgress.total} />}
            <div className="flex justify-end mb-2">
                <button
                    onClick={refreshAllLive}
                    disabled={liveRunning || loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    title="Re-fetch live marketplace data for every product in this view"
                >
                    <RefreshCw size={12} /> Fetch Live Data Again
                </button>
            </div>
            <div className="relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"
                >
                    ✕
                </button>
                <DealsViewer
                    deals={deals}
                    loading={loading}
                    title={storefront.store_name || 'Storefront Deals'}
                    showFilters={true}
                    pageName={"storefront"}
                    user={user}
                />
            </div>
        </div>
    );
};

const StorefrontEmailSettings = ({ user }) => {
    const [mode, setMode] = useState(null); // null=loading, 'default'|'none'|'custom'
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [open, setOpen] = useState(false);
    const accountEmail = user?.email || '';

    useEffect(() => {
        authService.authenticatedFetch(API_BASE + '/api/storefront/notification-emails')
            .then(r => r.json())
            .then(data => {
                const prefs = data.notification_emails;
                if (prefs === null || prefs === undefined) {
                    setMode('default');
                    setEmails([]);
                } else if (prefs.length === 0) {
                    setMode('none');
                    setEmails([]);
                } else {
                    setMode('custom');
                    setEmails(prefs);
                }
            })
            .catch(() => setMode('default'));
    }, []);

    const save = async () => {
        setSaving(true);
        let payload;
        if (mode === 'default') payload = null;
        else if (mode === 'none') payload = [];
        else payload = emails;

        await authService.authenticatedFetch(API_BASE + '/api/storefront/notification-emails', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emails: payload })
        });
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const addEmail = () => {
        const trimmed = newEmail.trim();
        if (!trimmed || emails.includes(trimmed)) return;
        if (!/^[^@]+@[^@]+\.[^@]+$/.test(trimmed)) { alert('Invalid email'); return; }
        setEmails(prev => [...prev, trimmed]);
        setNewEmail('');
    };

    const removeEmail = (email) => setEmails(prev => prev.filter(e => e !== email));

    return (
        <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition"
            >
                <div className="flex items-center gap-3">
                    <Bell className="text-teal-600" size={20} />
                    <div>
                        <span className="font-semibold text-gray-800">New Product Email Notifications</span>
                        <span className="ml-3 text-sm text-gray-500">
                            {mode === 'default' && `Sending to: ${accountEmail}`}
                            {mode === 'none' && 'Notifications disabled'}
                            {mode === 'custom' && `Sending to: ${emails.join(', ')}`}
                            {mode === null && '...'}
                        </span>
                    </div>
                </div>
                {open ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </button>

            {open && (
                <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
                    <p className="text-sm text-gray-600">
                        Choose where to send emails when a tracked storefront adds new products.
                    </p>

                    <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 transition"
                            style={{ borderColor: mode === 'default' ? 'rgb(13,148,136)' : '#e5e7eb', background: mode === 'default' ? 'rgb(240,253,250)' : '' }}
                            onClick={() => setMode('default')}>
                            <input type="radio" readOnly checked={mode === 'default'} className="accent-teal-600" />
                            <div>
                                <div className="font-semibold text-gray-800">My account email</div>
                                <div className="text-sm text-gray-500">{accountEmail}</div>
                            </div>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 transition"
                            style={{ borderColor: mode === 'none' ? 'rgb(13,148,136)' : '#e5e7eb', background: mode === 'none' ? 'rgb(240,253,250)' : '' }}
                            onClick={() => setMode('none')}>
                            <input type="radio" readOnly checked={mode === 'none'} className="accent-teal-600" />
                            <div>
                                <div className="font-semibold text-gray-800">Don't send emails</div>
                                <div className="text-sm text-gray-500">I'll check the app manually</div>
                            </div>
                        </label>

                        <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border-2 transition"
                            style={{ borderColor: mode === 'custom' ? 'rgb(13,148,136)' : '#e5e7eb', background: mode === 'custom' ? 'rgb(240,253,250)' : '' }}
                            onClick={() => setMode('custom')}>
                            <input type="radio" readOnly checked={mode === 'custom'} className="accent-teal-600 mt-1" />
                            <div className="flex-1">
                                <div className="font-semibold text-gray-800">Custom email address(es)</div>
                                <div className="text-sm text-gray-500 mb-2">Send to one or more specific addresses</div>

                                {mode === 'custom' && (
                                    <div className="space-y-2" onClick={e => e.stopPropagation()}>
                                        {emails.map(email => (
                                            <div key={email} className="flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-1.5">
                                                <span className="text-sm flex-1">{email}</span>
                                                <button onClick={() => removeEmail(email)} className="text-red-400 hover:text-red-600">
                                                    <XCircle size={16} />
                                                </button>
                                            </div>
                                        ))}
                                        <div className="flex gap-2">
                                            <input
                                                type="email"
                                                value={newEmail}
                                                onChange={e => setNewEmail(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && addEmail()}
                                                placeholder="add@email.com"
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                            <button onClick={addEmail} className="px-3 py-1.5 bg-teal-600 text-white rounded text-sm hover:bg-teal-700">
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>

                    <button
                        onClick={save}
                        disabled={saving || (mode === 'custom' && emails.length === 0)}
                        className="px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving ? <RefreshCw className="animate-spin" size={16} /> : saved ? <CheckCircle size={16} /> : null}
                        {saved ? 'Saved!' : 'Save'}
                    </button>
                    {mode === 'custom' && emails.length === 0 && (
                        <p className="text-xs text-red-500">Add at least one email address.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export { StorefrontEmailSettings };
export default StorefrontScanPage;